/**
 * Viewer dispatch for `WorkspaceFilePane`.
 *
 * Per ai-docs/lexide-layout-plan.md §3.5 + the premium-viewer wave
 * (2026-06-25), the pane dispatches by extension to specialized
 * lazy-loaded viewers. The premium wave replaced the placeholder stubs
 * AND the older AIEditor-as-MD-viewer path with:
 *
 *   - **MarkdownHtmlViewer** — react-markdown + remark-gfm + remark-math
 *                              + rehype-katex / -highlight / -raw. Pure
 *                              READ-ONLY (AIEditor stays for petition
 *                              editing in OTHER contexts).
 *   - **PdfViewer** — react-pdf (pdf.js v5) with full toolbar:
 *                     page nav, zoom (50%–300%), rotate, search,
 *                     download, print. Text + annotation layers wired
 *                     so users can select / copy / click links inside
 *                     the document.
 *   - **DocxViewer** — docx-preview (renderAsync) with high-fidelity
 *                      OOXML → HTML: tables, images, page breaks,
 *                      headers / footers / footnotes / endnotes.
 *   - **ImageViewer** — `<img src={blobUrl}>` with object-fit:contain.
 *   - **TextViewer** — UTF-8-decoded `<pre>` for any remaining text-y
 *                      extension. Routes .yaml/.yml/.json/.csv/.txt/
 *                      .log/.xml/.html through one component.
 *   - **ExtractedSourceViewer** (2026-07-31) — the DEFAULT branch. For an
 *                      ingested `sources/*` original the browser cannot
 *                      render (.xlsx/.pptx/.msg/…), it reads the hidden
 *                      `.processed.md` machine-extraction companion and
 *                      leads with a summary; the old "no preview
 *                      available" text survives only where there is
 *                      genuinely nothing to show.
 *
 * Every bar also carries a DOWNLOAD control (`ViewerInfoBar`'s `download`
 * prop) except PDF and DOCX, which own one inside their own toolbars.
 *
 * Lazy-loading: PdfViewer, DocxViewer, and MarkdownHtmlViewer are wrapped
 * in `React.lazy()` so their large dep chunks (pdf.js worker ~1.4 MB,
 * docx-preview ~200 KB, react-markdown plugin stack ~150 KB) only ship
 * when the user actually opens a file of that type. ImageViewer and
 * TextViewer stay eagerly imported — they have no heavy deps.
 *
 * The dispatch-testid contract is preserved across loading/error/loaded
 * states so existing E2E specs continue to assert against the same
 * stable per-extension testid.
 */

import { Suspense, lazy, useEffect, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, FileText, Loader2 } from "lucide-react";
import { basename, type FileRef } from "@/stores/useWorkspaceFileStore";
import { useFileBytes } from "@/hooks/useFileBytes";
import { describeViewerError } from "@/components/workspace/viewer-error";
import { useFileDownload } from "@/hooks/useFileDownload";
import { ViewerInfoBar, type ViewerDownload } from "./viewers/ViewerInfoBar";
import { ExtractedSourceViewer } from "./viewers/ExtractedSourceViewer";

interface ViewerProps {
  file: FileRef;
  /** Wired by the WorkspaceFilePane shell. Viewers that opt out of
   *  the shell topbar (see `viewerOwnsTopbar`) MUST render their
   *  own close affordance — the InfoBar in MarkdownHtmlViewer hosts
   *  an inline [×] when this is provided. */
  onClose?: () => void;
}

/** Universal flag — every viewer now renders its own IDE-style
 *  `ViewerInfoBar` chrome at the top (filename chip + click-for-
 *  details + [×] close + optional viewer-specific chips). The shell
 *  therefore never renders its own topbar. Kept as a function (vs
 *  inlined `true`) so a future "naked" viewer mode (image
 *  full-bleed, etc.) can opt back out without touching the shell. */
export function viewerOwnsTopbar(_ext: string): boolean {
  return true;
}

// ── Lazy chunks ────────────────────────────────────────────────────
// Each of these is the entry point for a sizeable transitive dep tree;
// React.lazy + Suspense means the chunk only loads when the dispatch
// switch actually routes here.
const LazyMarkdownHtmlViewer = lazy(() =>
  import("./viewers/MarkdownHtmlViewer").then((m) => ({
    default: m.MarkdownHtmlViewer,
  })),
);
const LazyPdfViewer = lazy(() =>
  import("./viewers/PdfViewer").then((m) => ({ default: m.PdfViewer })),
);
const LazyDocxViewer = lazy(() =>
  import("./viewers/DocxViewer").then((m) => ({ default: m.DocxViewer })),
);
// ⚠ LAZY on purpose. `AudioViewer` is eagerly imported (it had no heavy deps),
// and the transcript pulls the react-markdown stack — importing it eagerly
// would drag that chunk into the main bundle for every session that never
// opens an audio file, quietly undoing the split this docblock describes.
const LazyMediaTranscript = lazy(() =>
  import("./viewers/MediaTranscript").then((m) => ({ default: m.MediaTranscript })),
);

/** Loading + error chrome shared across viewers. State is exposed via
 *  `data-viewer-state` (not its own testid) so the containing viewer's
 *  dispatch testid stays the stable signal E2E specs assert on. */
function ViewerStatus({
  state,
  message,
}: {
  state: "loading" | "error" | "empty";
  message: string;
}) {
  const Icon =
    state === "loading" ? Loader2 : state === "error" ? AlertTriangle : FileText;
  return (
    <div
      data-viewer-state={state}
      className="flex h-full min-h-0 flex-col items-center justify-center gap-2 p-6 text-sm text-slate-500 dark:text-slate-400"
    >
      <Icon
        size={20}
        className={state === "loading" ? "animate-spin" : undefined}
      />
      <p className="max-w-md text-center text-xs">{message}</p>
    </div>
  );
}

/**
 * Standard download descriptor for the shared `ViewerInfoBar`.
 *
 * Every viewer that renders the bar gets one, so "save the original" is
 * a property of the pane rather than something a given viewer happened
 * to implement — the gap the operator hit on 2026-07-31 was exactly a
 * viewer that had no such control while its own message told the lawyer
 * to use one. PDF and DOCX are the two exceptions: each already owns a
 * download inside its own toolbar, and a second one would be redundant.
 *
 * The read is deferred to the click (`useFileDownload`), and react-query
 * keys the fetch on the same `path`, so a viewer that ALREADY loaded the
 * bytes for display serves the download straight from cache.
 *
 * Filename note: this uses the frozen basename. For an ingested source
 * the uploader's own filename is nicer, but it lives in the
 * `.processed.md` companion — `ExtractedSourceViewer` reads that already
 * and passes the better name; the generic viewers do not pay an extra
 * read just to improve a filename.
 */
function useViewerDownload(file: FileRef): ViewerDownload {
  const filename = basename(file.path);
  const dl = useFileDownload(file.path, filename);
  return { filename, onDownload: dl.request, pending: dl.pending };
}

/** Suspense fallback shown while a lazy viewer chunk is loading. */
function LazyChunkFallback() {
  const { t } = useTranslation();
  return (
    <ViewerStatus
      state="loading"
      message={t("workspace.filePane.viewer.loading")}
    />
  );
}

// ── Per-extension wrappers ─────────────────────────────────────────
// Each wrapper carries the stable per-extension dispatch testid so
// E2E specs find the dispatch signal regardless of internal loading /
// error / lazy-chunk-loading state.

export function MarkdownViewer({ file, onClose }: ViewerProps) {
  return (
    <div
      data-testid="workspace-file-pane-viewer-markdown"
      className="flex h-full min-h-0 flex-col"
    >
      <Suspense fallback={<LazyChunkFallback />}>
        <LazyMarkdownHtmlViewer file={file} onClose={onClose} />
      </Suspense>
    </div>
  );
}

export function PdfViewer({ file, onClose }: ViewerProps) {
  return (
    <div
      data-testid="workspace-file-pane-viewer-pdf"
      className="flex h-full min-h-0 flex-col"
    >
      {/* No `download` here on purpose: PdfViewer's own toolbar already
          carries one alongside page-nav / zoom / rotate / print, and a
          second control in the bar above it would be redundant. Same
          reasoning as DocxViewer, which slots its own into the bar. */}
      <ViewerInfoBar file={file} onClose={onClose} />
      <Suspense fallback={<LazyChunkFallback />}>
        <LazyPdfViewer file={file} />
      </Suspense>
    </div>
  );
}

export function DocxViewer({ file, onClose }: ViewerProps) {
  return (
    <div
      data-testid="workspace-file-pane-viewer-docx"
      className="flex h-full min-h-0 flex-col"
    >
      {/* Single consolidated bar (operator 2026-07-30): the INNER docx
          viewer renders the shared ViewerInfoBar itself so size + download
          live on the SAME line as title + close — its old second toolbar
          line was pure redundancy. */}
      <Suspense fallback={<LazyChunkFallback />}>
        <LazyDocxViewer file={file} onClose={onClose} />
      </Suspense>
    </div>
  );
}

export function ImageViewer({ file, onClose }: ViewerProps) {
  const { t } = useTranslation();
  const { url, isPending, isError, error } = useFileBytes(file.path);
  const download = useViewerDownload(file);

  return (
    <div
      data-testid="workspace-file-pane-viewer-image"
      className="flex h-full min-h-0 flex-col"
    >
      <ViewerInfoBar file={file} onClose={onClose} download={download} />
      <div className="flex min-h-0 flex-1 items-center justify-center bg-slate-50 dark:bg-slate-900">
        {isPending ? (
          <ViewerStatus state="loading" message={t("workspace.filePane.viewer.loading")} />
        ) : isError ? (
          <ViewerStatus state="error" message={describeViewerError(t, error, file.path)} />
        ) : !url ? (
          <ViewerStatus state="empty" message={t("workspace.filePane.viewer.empty")} />
        ) : (
          <img
            data-testid="workspace-file-pane-viewer-image-img"
            src={url}
            alt={file.title ?? file.path}
            className="max-h-full max-w-full object-contain"
          />
        )}
      </div>
    </div>
  );
}

/**
 * Audio viewer — native HTML5 `<audio controls>`. Streams from the
 * blob URL produced by `useFileBytes`. No waveform / scrubber UI yet
 * — the operator confirmed the native browser controls are enough for
 * day-one (intake-audio's `.processed.md` transcript sibling carries
 * the searchable content; this viewer is for playback verification).
 *
 * Supported extensions: mp3, wav, m4a, webm, ogg, oga, opus, flac, aac.
 * The runtime must return a matching `audio/*` MIME (see
 * `READ_MIME_BY_EXT` in the lexiun-core gateway).
 *
 * Renders the STT TRANSCRIPT beneath the player (`MediaTranscript`). This
 * docblock previously argued the native controls were "enough for day-one"
 * because the companion "carries the searchable content" — an argument that
 * quietly equated *indexed* with *readable*. For a voice note whose WORDS are
 * the evidence, a player with no text is a dead end.
 */
export function AudioViewer({ file, onClose }: ViewerProps) {
  const { t } = useTranslation();
  const { url, isPending, isError, error } = useFileBytes(file.path);
  const download = useViewerDownload(file);
  return (
    <div
      data-testid="workspace-file-pane-viewer-audio"
      className="flex h-full min-h-0 flex-col"
    >
      <ViewerInfoBar file={file} onClose={onClose} download={download} />

      {/* ⚖ THE TRANSCRIPT IS THE HERO — operator ruling 2026-08-11, inverting
          the first cut. The words are what the lawyer came for; the player is
          the instrument for checking them. So the transcript takes the whole
          reading area and SCROLLS, and the player is pinned below.

          The first cut had it the other way round — a player floating in a
          large empty pane over a cramped transcript strip — which allocated
          the space in inverse proportion to what is read. */}
      <div
        data-testid="workspace-file-pane-viewer-audio-body"
        className="min-h-0 flex-1 overflow-auto bg-white dark:bg-coffee-950"
      >
        <Suspense fallback={null}>
          <LazyMediaTranscript file={file} />
        </Suspense>
      </div>

      {/* FIXED player. `shrink-0` so a long transcript scrolls PAST it rather
          than pushing it off: the control that plays the evidence must stay
          reachable at every scroll position. */}
      <div className="shrink-0 border-t border-cream-200/70 bg-slate-50 px-6 py-3 dark:border-coffee-700/50 dark:bg-slate-900">
        {isPending ? (
          <ViewerStatus state="loading" message={t("workspace.filePane.viewer.loading")} />
        ) : isError ? (
          <ViewerStatus state="error" message={describeViewerError(t, error, file.path)} />
        ) : !url ? (
          <ViewerStatus state="empty" message={t("workspace.filePane.viewer.empty")} />
        ) : (
          <audio
            data-testid="workspace-file-pane-viewer-audio-player"
            src={url}
            controls
            preload="metadata"
            className="mx-auto w-full max-w-xl"
          >
            {/* Browsers that don't support <audio> fall through to this
                text — the same fallback the spec recommends. */}
            {t("workspace.filePane.viewer.audioFallback")}
          </audio>
        )}
      </div>
    </div>
  );
}

/**
 * Unsupported viewer — last-resort fallback when the file's extension
 * isn't on any viewer's allowlist. Replaces the older behaviour
 * (best-effort UTF-8 decode in TextViewer, which rendered binary
 * uploads as long pages of mojibake — operator-flagged 2026-06-26).
 *
 * **No longer a dead end (2026-07-31).** It now delegates to
 * `ExtractedSourceViewer`, which — for an ingested `sources/*` original
 * — reads the hidden `.processed.md` machine-extraction companion and
 * renders THAT, so a `.xlsx` shows its sheets instead of an apology. The
 * apology survives for everything with no usable extraction (a file
 * outside `sources/`, a legacy source with no companion, or an intake
 * that failed), and it now ships with the download control its own text
 * has always told the lawyer to use.
 *
 * The no-eager-bytes property is preserved: the original's bytes are
 * read only if the lawyer actually clicks download.
 */
export function UnsupportedViewer({ file, onClose }: ViewerProps) {
  return <ExtractedSourceViewer file={file} onClose={onClose} />;
}

/**
 * Text viewer — UTF-8 decode of the byte blob into a `<pre>`. Used for
 * .txt / .yaml / .json / .csv / anything without a specialized viewer.
 * Truncates display past `TEXT_DISPLAY_CAP` chars so a mistakenly-
 * routed large text body doesn't lock the renderer.
 */
const TEXT_DISPLAY_CAP = 256 * 1024;

export function TextViewer({ file, onClose }: ViewerProps) {
  const { t } = useTranslation();
  const { url, isPending, isError, error, byteLength } = useFileBytes(
    file.path,
  );
  const [text, setText] = useState<string | null>(null);
  const [decodeError, setDecodeError] = useState<Error | null>(null);
  const download = useViewerDownload(file);

  useEffect(() => {
    if (!url) {
      setText(null);
      setDecodeError(null);
      return;
    }
    let cancelled = false;
    setText(null);
    setDecodeError(null);
    fetch(url)
      .then((r) => r.text())
      .then((decoded) => {
        if (!cancelled) setText(decoded);
      })
      .catch((err) => {
        if (!cancelled) setDecodeError(err as Error);
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  const dispatchTestId = dispatchTestIdForPath(file.path);

  if (isPending || (url && text === null && !decodeError)) {
    return (
      <div data-testid={dispatchTestId} className="flex h-full min-h-0 flex-col">
        <ViewerInfoBar file={file} onClose={onClose} download={download} />
        <ViewerStatus state="loading" message={t("workspace.filePane.viewer.loading")} />
      </div>
    );
  }
  if (isError || decodeError) {
    return (
      <div data-testid={dispatchTestId} className="flex h-full min-h-0 flex-col">
        <ViewerInfoBar file={file} onClose={onClose} download={download} />
        <ViewerStatus
          state="error"
          message={describeViewerError(t, error ?? decodeError, file.path)}
        />
      </div>
    );
  }
  if (text === null) {
    return (
      <div data-testid={dispatchTestId} className="flex h-full min-h-0 flex-col">
        <ViewerInfoBar file={file} onClose={onClose} download={download} />
        <ViewerStatus state="empty" message={t("workspace.filePane.viewer.empty")} />
      </div>
    );
  }

  const truncated = text.length > TEXT_DISPLAY_CAP;
  const display = truncated ? text.slice(0, TEXT_DISPLAY_CAP) : text;
  return (
    <div data-testid={dispatchTestId} className="flex h-full min-h-0 flex-col">
      <ViewerInfoBar file={file} onClose={onClose} download={download} />
      <pre
        data-testid="workspace-file-pane-viewer-text-pre"
        data-byte-length={byteLength ?? undefined}
        className="flex-1 overflow-auto whitespace-pre-wrap break-words p-4 font-mono text-xs leading-relaxed text-slate-700 dark:text-slate-200"
      >
        {display}
      </pre>
      {truncated && (
        <div
          data-testid="workspace-file-pane-viewer-text-truncated"
          className="border-t border-slate-200 bg-amber-50 px-4 py-2 text-[11px] text-amber-800 dark:border-slate-800 dark:bg-amber-900/20 dark:text-amber-200"
        >
          {t("workspace.filePane.viewer.truncated", {
            shown: TEXT_DISPLAY_CAP,
            total: byteLength,
          })}
        </div>
      )}
    </div>
  );
}

/** Per-extension dispatch testid for `TextViewer` consumers. */
function dispatchTestIdForPath(path: string): string {
  const dot = path.lastIndexOf(".");
  const ext = dot < 0 ? "" : path.slice(dot + 1).toLowerCase();
  if (ext === "yaml" || ext === "yml") return "workspace-file-pane-viewer-yaml";
  return "workspace-file-pane-viewer-text";
}

type ViewerComponent = (props: ViewerProps) => ReactElement;

/** Map file extension → viewer component. Unknown extensions fall
 *  back to `UnsupportedViewer` (shows a "preview not available"
 *  message instead of UTF-8-decoding binary as mojibake — operator-
 *  flagged 2026-06-26). Keep in sync with `extension()` values
 *  returned by `useWorkspaceFileStore` + the runtime's
 *  `READ_MIME_BY_EXT` map in `document.ts`. */
export function viewerForExtension(ext: string): ViewerComponent {
  switch (ext) {
    case "md":
    case "markdown":
      return MarkdownViewer;
    case "pdf":
      return PdfViewer;
    case "docx":
      return DocxViewer;
    case "png":
    case "jpg":
    case "jpeg":
    case "gif":
    case "webp":
    case "svg":
      return ImageViewer;
    case "mp3":
    case "wav":
    case "m4a":
    case "webm":
    case "ogg":
    case "oga":
    // ⚠ `.opus` is what a WhatsApp voice note IS — every audio message in an
    // exported conversation carries this extension, so leaving it out sent the
    // single most common audio file in the corpus to `UnsupportedViewer`
    // (measured 2026-08-10). Opus-in-Ogg is natively decodable in every
    // browser this app supports; the runtime must also serve an `audio/*` MIME
    // for it (`READ_MIME_BY_EXT`) or the element gets octet-stream bytes.
    case "opus":
    case "flac":
    case "aac":
      return AudioViewer;
    case "yaml":
    case "yml":
    case "json":
    case "csv":
    case "txt":
    case "log":
    case "xml":
    case "html":
      return TextViewer;
    default:
      return UnsupportedViewer;
  }
}
